/* global alert */
import React, { useCallback, useContext, useEffect, useMemo, useState, useRef } from 'react';
import AppContext from './context/AppContext';
import SiteReview from './SiteReview';
import '../styles/SiteReviews.css';
import { Button } from 'react-bootstrap';
import { bool } from 'prop-types';
import { MdArrowBackIos, MdArrowForwardIos } from 'react-icons/md';

const BASE_REVIEW_WIDTH = 300;
const BASE_REVIEW_WIDTH_MOBILE = 100;
const BASE_SYM = 'px';
const BASE_SYM_MOB = '%';
const REVIEWS_PER_VIEW = 3;
const REVIEWS_PER_VIEW_MOBILE = 1;
const AUTO_SWITCH_MS = 3500;
const ANIMATION_DURATION = 200;
const TRANSLATE_LEFT_SIGN = -1;
const MOVE_THRESHOLD = 50;
const SITE_REVIEWS_REPLICATION = 3;

export default function SiteReviews({ isMobile }) {
    const { appContext } = useContext(AppContext);
    const { shopperApprovedSealId, shopperApprovedReviewsUrl } = appContext.config;
    const [siteReviews, setSiteReviews] = useState([]);
    const [animate, setAnimate] = useState(true);
    const [startX, setStartX] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const moveCount = useRef(0);
    const slidingDisabled = useRef(false);
    const [translateX, setTranslateX] = useState(0);
    const widthByDevice = useMemo(() => isMobile
        ? BASE_REVIEW_WIDTH_MOBILE
        : BASE_REVIEW_WIDTH,
    [BASE_REVIEW_WIDTH_MOBILE, BASE_REVIEW_WIDTH, isMobile]);
    const reviewsByDevice = useMemo(() => isMobile
        ? REVIEWS_PER_VIEW_MOBILE
        : REVIEWS_PER_VIEW,
    [REVIEWS_PER_VIEW_MOBILE, REVIEWS_PER_VIEW, isMobile]);
    const sym = useMemo(() => isMobile
        ? BASE_SYM_MOB
        : BASE_SYM,
    [BASE_SYM_MOB, BASE_SYM, isMobile]);

    const isSlideAble = useMemo(() => (siteReviews.length / SITE_REVIEWS_REPLICATION) > reviewsByDevice, [siteReviews.length, reviewsByDevice]);
    const fetchSiteReviews = useCallback(async () => {
        try {
            const params = new URLSearchParams({
                limit: 6,
                page: 0,
            }).toString();
            const response = await fetch(new URL(`/api/reviews/siteReviews/?${params}`, process.env.APP_URL), {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            const data = await response.json();
            if (data.siteReviews) {
                moveCount.current = data.siteReviews.length ?? 0;
                setSiteReviews([...data.siteReviews, ...data.siteReviews, ...data.siteReviews]);
                setTranslateX(data.siteReviews.length * widthByDevice * TRANSLATE_LEFT_SIGN);
            }
        } catch (e) {
            console.info(e);
        }
    }, []);
    useEffect(() => {
        fetchSiteReviews();
    }, [fetchSiteReviews]);
    const resetSliderState = () => {
        slidingDisabled.current = true;
        setTimeout(() => {
            const originalReviewsSize = siteReviews.length / SITE_REVIEWS_REPLICATION;
            setTranslateX(originalReviewsSize * widthByDevice * TRANSLATE_LEFT_SIGN);
            setAnimate(false);
            moveCount.current = originalReviewsSize;
        }, ANIMATION_DURATION);
    };

    const next = useCallback(() => {
        if (!siteReviews.length || !isSlideAble || slidingDisabled.current) {
            return;
        }
        setTranslateX((prev) => prev - widthByDevice);
        moveCount.current++;
        if (Math.abs(moveCount.current) === ((siteReviews.length) - (siteReviews.length / SITE_REVIEWS_REPLICATION))) {
            resetSliderState();
        }
    }, [siteReviews.length, isSlideAble]);
    const prev = useCallback(() => {
        if (!siteReviews.length || !isSlideAble || slidingDisabled.current) {
            return;
        }
        setTranslateX((prev) => prev + widthByDevice);
        moveCount.current--;
        if (moveCount.current === 0) {
            resetSliderState();
        }
    }, [siteReviews.length, isSlideAble]);
    useEffect(() => {
        let autoSwitchTimer = null;
        const autoSwitchFunc = () => {
            next();
        };
        autoSwitchTimer = setTimeout(autoSwitchFunc, AUTO_SWITCH_MS);
        return () => {
            clearTimeout(autoSwitchTimer);
        };
    }, [next]);
    const onContextMenu = useCallback((e) => {
        e.preventDefault();
        const d = new Date();
        alert(`Copying Prohibited by Law - This image and all included logos are copyrighted by Shopper Approved (c) ${d.getFullYear()}.`);
        return false;
    }, []);
    useEffect(() => {
        if (!animate) {
            setTimeout(() => {
                setAnimate(true);
            }, ANIMATION_DURATION);
        } else {
            slidingDisabled.current = false;
        }
    }, [animate]);
    const handleTouchStart = (e) => {
        setStartX(e.touches[0].clientX);
        setIsDragging(true);
    };
    const handleTouchMove = (e) => {
        if (!isDragging) {
            return;
        }
        const moveX = e.touches[0].clientX;
        if (startX - moveX > MOVE_THRESHOLD) {
            next();
            setIsDragging(false);
        } else if (moveX - startX > MOVE_THRESHOLD) {
            prev();
            setIsDragging(false);
        }
    };
    const handleMouseDown = (e) => {
        setStartX(e.clientX);
        setIsDragging(true);
    };
    const handleMouseMove = (e) => {
        if (!isDragging) {
            return;
        }
        const moveX = e.clientX;
        if (startX - moveX > MOVE_THRESHOLD) {
            next();
            setIsDragging(false);
        } else if (moveX - startX > MOVE_THRESHOLD) {
            prev();
            setIsDragging(false);
        }
    };
    const handleMouseUp = () => {
        setIsDragging(false);
    };
    return (<div
        className={'site-reviews-container'}>
        <h3
            className={'site-reviews-title'}
        >
            SELLER REVIEWS
        </h3>
        <div
            className={'site-reviews-wrapper'}
        >
            <button
                style={{ all: 'unset' }}
                onContextMenu={onContextMenu}>
                <div>
                    <a
                        href={shopperApprovedReviewsUrl}
                        rel={'noopener noreferrer'}
                        target={'_blank'}
                    >
                        <img
                            src={`https://ab-bhs-ca-3.abtra.ovh/shopperapproved/badges/badge-${shopperApprovedSealId}.png`}
                            loading={'lazy'}
                            alt={'Shopper Approved badge'}
                            width={230}
                            height={172}
                            crossOrigin={'true'}
                        />
                    </a>
                </div>
            </button>
            <div
                style={{ flexGrow: 1 }}
            >
                {siteReviews.length > 0 && (<div
                    className={'site-reviews'}>
                    <Button
                        onClick={prev}
                        as={MdArrowBackIos} />
                    <button
                        style={{
                            all: 'unset',
                            display: 'block',
                        }}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}>
                        <div
                            className={'site-reviews--inner'}
                            style={{
                                maxWidth: `${widthByDevice * reviewsByDevice}${sym}`,
                            }}>
                            <div
                                className={'site-reviews--slider'}
                                style={{
                                    transform: `translateX(${translateX}px)`,
                                    transition: animate ? `transform ${ANIMATION_DURATION}ms ease-in-out` : 'none',
                                }}>
                                {siteReviews.map((review) => (<SiteReview
                                    key={review.url}
                                    reviewData={review}
                                    width={widthByDevice}
                                    symbol={sym} />))}
                            </div>
                        </div>
                    </button>
                    <Button
                        onClick={next}
                        as={MdArrowForwardIos} />
                </div>)}
                <a
                    href={shopperApprovedReviewsUrl}
                    className={'site-reviews-see-more'}
                    rel={'noopener noreferrer'}
                    target={'_blank'}
                >
                    See more reviews »
                </a>
            </div>
        </div>
    </div>);
}

SiteReviews.propTypes = {
    isMobile: bool,
};